import React, { useContext, useEffect, useRef, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { DateTime } from "luxon";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Timeframe } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/sudo_filters_pb";
import {
  AuditLog,
  AuditLogsFilters,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_audit_logs_pb";

import { grpcErrorToString } from "../constants/grpc-errors";
import { DEFAULT_REGION_CODE } from "../constants/index";
import { actionTypeToString } from "../constants/periscope-actions";
import {
  convertTimestampToDate,
  convertToTimestamp,
  formatDateTimeDisplay,
} from "../helpers/time";
import mainStyles from "../styles";
import { RegionCode } from "../types/region";
import { listAuditLogs } from "api/endpoints/activity-history";
import { callOptionsHeaders } from "api/helpers";
import CheckboxFilter from "components/shared/checkbox-filter";
import DatePicker from "components/shared/date-picker";
import FilterTypeAutoComplete, {
  FilterTypeItem,
} from "components/shared/filter-type-autocomplete";
import {
  ActionTypeItems,
  ActionTypeItemsFlat,
} from "constants/periscope-actions";
import { SnackbarContext } from "context/snackbar-context";
import { JsonTreeViewer } from "pages/dev-tools/components/json-tree-viewer";

// This abomination is needed because in Typescript, enums will be double mapped in the emitted object
// e.g. {  "ACTION_TYPE_UNSPECIFIED": 0, 0: "ACTION_TYPE_UNSPECIFIED", }
// This wasn't happening with gRPCWeb definition because of some differences with how the enums are generated
// https://stackoverflow.com/questions/43100718/typescript-enum-to-object-array
const stringIsNumber = (value: string) => isNaN(Number(value)) === false;
const allActionTypes: ActionType[] = Object.keys(ActionType)
  .filter((key) => !stringIsNumber(key))
  .map((key) => ActionType[key as keyof typeof ActionType]);

const allStatusTypes = Object.keys(grpcErrorToString).map((s) => {
  return Number(s);
});

interface AuditFilters {
  limit: number;
  offset: number;
  scUserEmail: string;
  referenceId: string;
  actions: ActionType[];
  grpcStatuses: number[];
  from?: Date;
  to?: Date;
}

const PeriscopeHistory = () => {
  const classes = mainStyles();
  const AUDIT_LOGS_PAGE_SIZE = 20;
  const now = DateTime.now();
  const defaultFrom = now.startOf("day").minus({ days: 30 });

  const [regionCode, setRegionCode] = useState<RegionCode>(DEFAULT_REGION_CODE);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const defaultFilters = {
    limit: AUDIT_LOGS_PAGE_SIZE,
    offset: 0,
    scUserEmail: "",
    referenceId: "",
    actions: allActionTypes,
    grpcStatuses: allStatusTypes,
    from: defaultFrom.toJSDate(),
    to: now.endOf("day").toJSDate(),
  };

  const [auditLogFilters, setAuditLogFilters] =
    useState<AuditFilters>(defaultFilters);

  const [nextPageToken, setNextPageToken] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const shouldRenderSeeMoreBtn =
    !isLoading && auditLogs.length !== 0 && nextPageToken;
  const shouldRenderCaughtUpMsg =
    !isLoading && auditLogs.length !== 0 && !nextPageToken;

  const [actionCheckboxFilters, setActionCheckboxFilters] =
    useState<ActionType[]>(allActionTypes);

  const [actionTypeSelected, setActionTypeSelected] =
    useState<FilterTypeItem[]>(ActionTypeItemsFlat);

  const [statusCheckboxFilters, setStatusCheckboxFilters] =
    useState<number[]>(allStatusTypes);

  const isFirstRender = useRef(true);
  const { showError } = useContext(SnackbarContext);

  const handleRegionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRegionCode(event.target.value as RegionCode);
  };

  const updateAuditLogFilters = (
    key: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setAuditLogFilters({
      ...auditLogFilters,
      [key]: event.target.value,
    });
  };

  const handleUpdateDate = (key: string, updatedDate: Date) => {
    if (key === "from") {
      setAuditLogFilters({
        ...auditLogFilters,
        from: updatedDate,
      });
    }
    if (key === "to") {
      setAuditLogFilters({
        ...auditLogFilters,
        to: updatedDate,
      });
    }
  };

  const getAuditLogs = (
    pageSize: number,
    pageToken?: string,
    filters?: AuditFilters
  ) => {
    setIsLoading(true);
    const newFilters = new AuditLogsFilters({
      actions: actionTypeSelected.map((item) => item.Value as ActionType),
      grpcStatuses: statusCheckboxFilters,
    });
    if (filters) {
      newFilters.limit = filters.limit || AUDIT_LOGS_PAGE_SIZE;
      newFilters.scUserEmail = filters.scUserEmail;
      newFilters.referenceId = filters.referenceId;
    }
    if (filters?.from !== undefined || filters?.to !== undefined) {
      const timeframe = new Timeframe();
      if (filters.from !== undefined) {
        const formattedFrom = convertToTimestamp(filters.from);
        timeframe.from = formattedFrom;
      }
      if (filters.to !== undefined) {
        const formattedTo = convertToTimestamp(filters.to);
        timeframe.to = formattedTo;
      }
      newFilters.timeframe = timeframe;
    }

    listAuditLogs(
      {
        pageSize,
        pageToken,
        filters: newFilters,
      },
      callOptionsHeaders(regionCode)
    )
      .then((res) => {
        const loadingInitialAuditLog = pageToken === "";
        const records = res.auditLogs;
        const updatedAuditLog: AuditLog[] = [
          ...(loadingInitialAuditLog ? [] : auditLogs),
          ...records,
        ];
        setAuditLogs(updatedAuditLog);
        setNextPageToken(res.nextPageToken);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const loadAuditLogs = (pageToken?: string) => {
    getAuditLogs(AUDIT_LOGS_PAGE_SIZE, pageToken, auditLogFilters);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    loadAuditLogs("");
    // eslint-disable-next-line
  }, [actionCheckboxFilters, statusCheckboxFilters]);

  const handleSeeMoreResults = () => {
    loadAuditLogs(nextPageToken);
  };

  const clearSearch = () => {
    setAuditLogFilters(defaultFilters);
    setActionCheckboxFilters(allActionTypes);
    setActionTypeSelected(ActionTypeItems);
    setStatusCheckboxFilters(allStatusTypes);
  };

  const toggleRowExpansion = (index: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  const parseRequest = (request: string): unknown => {
    if (!request) return { message: "No request data" };
    try {
      return JSON.parse(request);
    } catch {
      return { rawRequest: request };
    }
  };

  const REQUEST_PREVIEW_MAX_LENGTH = 200;

  const getRequestPreview = (request: string): string => {
    if (!request) return "No request data available";

    // Limit preview to first REQUEST_PREVIEW_MAX_LENGTH characters
    const preview =
      request.length > REQUEST_PREVIEW_MAX_LENGTH
        ? `${request.substring(0, REQUEST_PREVIEW_MAX_LENGTH)}...`
        : request;

    try {
      const parsed = JSON.parse(request);
      const formatted = JSON.stringify(parsed, null, 2);
      return formatted.length > REQUEST_PREVIEW_MAX_LENGTH
        ? `${formatted.substring(0, REQUEST_PREVIEW_MAX_LENGTH)}...`
        : formatted;
    } catch {
      return preview;
    }
  };

  const auditLogRecord = (row: AuditLog, index: number) => {
    const isExpanded = expandedRows.has(index);
    return (
      <React.Fragment key={`${index}-${row.scUserEmail}`}>
        <TableRow>
          <TableCell align="left" style={{ verticalAlign: "top" }}>
            <Tooltip
              title={
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    margin: 0,
                  }}
                >
                  {getRequestPreview(row.request)}
                </Typography>
              }
              placement="right"
              arrow
            >
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => toggleRowExpansion(index)}
              >
                {isExpanded ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell align="left" style={{ verticalAlign: "top" }}>
            {row.scUserEmail}
            <br />
            <Typography variant="caption" gutterBottom>
              {row?.createdAt &&
                formatDateTimeDisplay(convertTimestampToDate(row?.createdAt))}
            </Typography>
          </TableCell>
          <TableCell align="left" style={{ verticalAlign: "top" }}>
            {actionTypeToString(row.action)}
          </TableCell>
          <TableCell align="left" style={{ verticalAlign: "top" }}>
            {row.referenceId}
          </TableCell>
          <TableCell align="left" style={{ verticalAlign: "top" }}>
            {grpcErrorToString[row.grpcStatus]}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <JsonTreeViewer
                  data={parseRequest(row.request)}
                  maxHeight={500}
                  defaultCollapsed={false}
                  enableCopy={true}
                  enableEdit={false}
                />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <form
          className={clsx(classes.spaceSearch)}
          noValidate
          autoComplete="off"
        >
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="region"
              name="region-radio-group"
              value={regionCode}
              row={true}
              onChange={handleRegionChange}
            >
              <FormControlLabel value="us" label="US" control={<Radio />} />
              <FormControlLabel value="au" label="AU" control={<Radio />} />
              <FormControlLabel value="eu" label="EU" control={<Radio />} />
            </RadioGroup>
          </FormControl>
          <TextField
            id="sc-user-email-input"
            label="Requested by"
            value={auditLogFilters.scUserEmail}
            onChange={(event) => updateAuditLogFilters("scUserEmail", event)}
            variant="outlined"
            size="small"
            inputProps={{ "data-testid": "sc-user-email-input" }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                getAuditLogs(
                  auditLogFilters.limit,
                  nextPageToken,
                  auditLogFilters
                );
              }
            }}
            sx={{ ...classes.textInput, ...classes.basicSpacing }}
          />
          <TextField
            id="reference-id-input"
            label="Reference ID"
            value={auditLogFilters.referenceId}
            onChange={(event) => updateAuditLogFilters("referenceId", event)}
            variant="outlined"
            size="small"
            inputProps={{ "data-testid": "reference-id-input" }}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                getAuditLogs(
                  auditLogFilters.limit,
                  nextPageToken,
                  auditLogFilters
                );
              }
            }}
            sx={{ ...classes.textInput, ...classes.basicSpacing }}
          />
        </form>
      </Grid>
      <Grid item xs={12}>
        <Box sx={classes.basicSpacing}>
          <FilterTypeAutoComplete
            label="Filter action types"
            items={ActionTypeItems}
            selectedItems={actionTypeSelected}
            setSelectedItems={setActionTypeSelected}
            testId="filter-periscope-actions-input"
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <form
          className={clsx(classes.spaceSearchNoMargin)}
          noValidate
          autoComplete="off"
        >
          <Box
            display="flex"
            gap={1}
            alignItems="center"
            flexWrap="wrap"
            justifyContent="center"
            sx={classes.basicSpacing}
          >
            <DatePicker
              label="From"
              size="small"
              defaultDateSelection={auditLogFilters.from}
              onChange={(date) => {
                handleUpdateDate("from", date);
              }}
            />
            <DatePicker
              label="To"
              size="small"
              defaultDateSelection={auditLogFilters.to}
              onChange={(date) => {
                handleUpdateDate("to", date);
              }}
            />
            <CheckboxFilter
              title="Filter status"
              filterList={statusCheckboxFilters}
              setFilterList={setStatusCheckboxFilters}
              allItems={allStatusTypes}
              displayNamesMapping={grpcErrorToString}
              testId="filter-status-input"
              size="small"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                getAuditLogs(AUDIT_LOGS_PAGE_SIZE, "", auditLogFilters);
              }}
              data-anchor="load-audit-logs-btn"
              data-testid="load-audit-logs-btn"
            >
              Load
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={clearSearch}
              data-testid="audit-logs-search-bar-clear-button"
            >
              Clear
            </Button>
          </Box>
        </form>
      </Grid>

      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table aria-label="sudo-history">
            <TableHead>
              <TableRow>
                <TableCell align="left" style={{ width: "50px" }} />
                <TableCell align="left">
                  <b>Requested By</b>
                </TableCell>
                <TableCell align="left">
                  <b>Action</b>
                </TableCell>
                <TableCell align="left">
                  <b>Reference ID</b>
                </TableCell>
                <TableCell align="left">
                  <b>Status</b>
                </TableCell>
              </TableRow>
            </TableHead>
            {isFirstRender.current && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={5}>
                    <b>Set filters to load results.</b>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
            <TableBody>
              {auditLogs.map((row, index) => auditLogRecord(row, index))}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {!isLoading && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      ...classes.paddingTopSmall,
                      ...classes.paddingBottomSmall,
                    }}
                  >
                    <Box display="flex" justifyContent="flex-end">
                      <Box>
                        {shouldRenderSeeMoreBtn && (
                          <Button
                            color="primary"
                            size="small"
                            onClick={handleSeeMoreResults}
                            data-testid="load-more-org-history-results-btn"
                          >
                            ...SEE MORE
                          </Button>
                        )}
                        {shouldRenderCaughtUpMsg && "You're all caught up"}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default PeriscopeHistory;
